import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { ChargePointChannelCreateController } from '@app/feature/charge-point/charge-point-channel-create/charge-point-channel-create.controller';
import {
  ChannelEditModeContextProvider,
  ChargePointChannelContextProvider,
  useChannelEditModeContext,
  useChargePointContext,
} from '@app/feature/charge-point/charge-point.context';

import { ChargePointChannelDetailsController } from '../charge-point-channel-details/charge-point-channel-details.controller';
import { ChargePointChannelDetailsPlaceholder } from '../charge-point-channel-details/charge-point-channel-details.placeholder';
import * as css from '../charge-point-details/charge-point-details.module.scss';

export const ChargePointChannelListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const { isEditMode, setIsEditMode } = useChannelEditModeContext();
  const [searchParams] = useSearchParams();
  const highlightedChannelId = searchParams.get('channelId');

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canCreate = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHANNELS,
        action: ActionsByResource.CHANNELS.CREATE,
      }),
    [userPermissions],
  );

  const canUpdate = useMemo(
    () =>
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHANNELS,
        action: ActionsByResource.CHANNELS.UPDATE,
      }),
    [userPermissions],
  );

  // ACTIONS: CREATE RESOURCE //////////////////////////////////////////////////

  const [actionCreateIsDialogOpen, setActionCreateIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <>
      {chargePoint.channels.length === 0 ? (
        <div className={css.placeholderWrapper}>
          <ChargePointChannelDetailsPlaceholder />
        </div>
      ) : (
        <Stack
          flex={1}
          gap={GapEnum.L}
          className={classNames(
            css.channelsContainer,
            isEditMode ? css.editMode : '',
          )}>
          <div className={css.threeColumns}>
            {chargePoint.channels.map((channel) => (
              <ChargePointChannelContextProvider
                key={channel.id}
                data={channel}>
                <ChargePointChannelDetailsController
                  highlighted={channel.id === highlightedChannelId}
                />
              </ChargePointChannelContextProvider>
            ))}
          </div>
          <Stack
            gap={GapEnum.M}
            justifyContent="space-between"
            direction="column">
            {canUpdate && (
              <Stack alignItems="start">
                <ButtonIcon
                  color={ButtonColor.Primary}
                  icon={isEditMode ? 'check-circle' : 'edit-05'}
                  type="button"
                  onClick={() => setIsEditMode((prev) => !prev)}
                />
              </Stack>
            )}
            {canCreate && isEditMode && (
              <ButtonIcon
                type="button"
                icon="plus"
                onClick={() => setActionCreateIsDialogOpen(true)}
              />
            )}
          </Stack>
        </Stack>
      )}
      <Dialog
        open={actionCreateIsDialogOpen}
        onClose={() => setActionCreateIsDialogOpen(false)}
        confirmClose
        size={DialogSize.LARGE}>
        <Dialog.Header
          onClose={() => setActionCreateIsDialogOpen(false)}
          title={t('$.charge-point-channel-create.title')}
          icon="electric-terminal"
        />
        <Dialog.Content>
          <ChargePointChannelCreateController
            onCancel={() => setActionCreateIsDialogOpen(false)}
            onSuccess={() => setActionCreateIsDialogOpen(false)}
          />
        </Dialog.Content>
      </Dialog>
    </>
  );
};

export const ChargePointChannelListControllerWithContext =
  (): React.JSX.Element => {
    return (
      <ChannelEditModeContextProvider>
        <ChargePointChannelListController />
      </ChannelEditModeContextProvider>
    );
  };
